import {useEffect, useState} from 'react';
import {ArrowLeft, Award, Book, CheckCircle, Clock, Target} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import SubmissionForm from './SubmissionForm';
import TeacherFeedback from './TeacherFeedback';
import SubmissionStatus from './SubmissionStatus';
import AssessmentWrapper from '../assessment/AssessmentWrapper';
import {formatDate} from '../../utils/helpers';
import {getApiServiceForGrade, isMiddleGrade, isPreparatoryGrade} from '../../utils/gradeUtils';

const AssignmentDetailsView = ({
                                   selectedAssignment,
                                   selectedClass,
                                   handleBack,
                                   showNotification
                               }) => {
    const [loading, setLoading] = useState(false);
    const [submission, setSubmission] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);

    // State for self-assessment form (for foundational stage)
    const [selfAssessmentForm, setSelfAssessmentForm] = useState({
        liked: null,
        easy: null,
        needed: []
    });

    // State for peer-assessment form (for foundational stage)
    const [peerAssessmentForm, setPeerAssessmentForm] = useState({
        friend_liked: null,
        friend_easy: null,
        friend_needed: []
    });

    // State for middle stage self-assessment form
    const [middleSelfAssessmentForm, setMiddleSelfAssessmentForm] = useState({
        reflections: {
            proud_of_effort: null,
            apply_to_real_life: null,
            motivated_to_learn: null
        },
        grid_selections: {
            awareness: [],
            sensitivity: [],
            creativity: []
        },
        learnings: {
            what_i_learnt: '',
            most_interesting: '',
            need_practice: '',
            need_help: ''
        }
    });

    // State for middle stage peer-assessment form
    const [middlePeerAssessmentForm, setMiddlePeerAssessmentForm] = useState({
        my_name: '',
        peer_name: '',
        engagement: {
            engaged_motivated: null,
            shared_thoughts: null
        },
        grid_selections: {
            awareness: [],
            sensitivity: [],
            creativity: []
        },
        peer_needs: {
            practice: '',
            help: ''
        }
    });

    // Determine which API service to use based on grade level
    const grade = selectedClass?.grade ? parseInt(selectedClass.grade, 10) : 1;
    const currentApiService = getApiServiceForGrade(grade);
    const isPreparatory = isPreparatoryGrade(grade);
    const isMiddle = isMiddleGrade(grade);

    // Fetch submission when component mounts
    useEffect(() => {
        if (selectedAssignment) {
            fetchSubmission(selectedAssignment.assignment_id);
        }
    }, [selectedAssignment]);

    // Fetch submission for an assignment
    const fetchSubmission = async (assignmentId) => {
        setLoading(true);
        try {
            const submissionData = await currentApiService.getSubmission(assignmentId);
            setSubmission(submissionData);
        } catch (err) {
            if (err.message.includes("No submission found")) {
                setSubmission(null);
            } else {
                showNotification(`Failed to fetch submission: ${err.message}`, "error");
                console.error(err);
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle file upload
    const handleFileChange = (e) => {
        if (e.target.files.length > 0) {
            const file = e.target.files[0];

            if (file.size > 10 * 1024 * 1024) {
                showNotification("File size exceeds 10MB limit. Please select a smaller file.", "error");
                e.target.value = null;
                return;
            }

            setUploadFile(file);
        }
    };

    // Handle clearing upload file (for conflicting submissions)
    const handleClearUploadFile = () => {
        console.log('Clearing upload file from parent component');
        setUploadFile(null);
        // Clear all file inputs
        const fileInputs = document.querySelectorAll('input[type="file"]');
        fileInputs.forEach(input => {
            input.value = '';
        });
    };

    // Handle unified submission (both file and text answers)
    const handleSubmit = async (dataOrFile, isTextSubmission = false) => {
        try {
            console.log('handleSubmit called with:', {
                isTextSubmission,
                dataType: isTextSubmission ? 'text' : 'file',
                data: isTextSubmission ? dataOrFile : dataOrFile?.name,
                uploadFileState: uploadFile?.name || 'none'
            });

            // CRITICAL: Clear conflicting data before submission
            if (isTextSubmission) {
                // For text submission, ensure no file is being sent
                if (uploadFile) {
                    console.log('Clearing uploadFile for text submission');
                    setUploadFile(null);
                    // Also clear any file input elements
                    const fileInputs = document.querySelectorAll('input[type="file"]');
                    fileInputs.forEach(input => {
                        input.value = '';
                    });
                }
            } else {
                // For file submission, we don't need to clear text answers as they're not sent
                // But log for debugging
                console.log('File submission - uploadFile state:', uploadFile?.name || 'none');
            }

            // Use the updated unified API service method
            const result = await currentApiService.submitAssignment(
                selectedAssignment.assignment_id,
                dataOrFile,
                isTextSubmission
            );

            console.log('Submission result:', result);

            // Clear upload file state if it was a file submission
            if (!isTextSubmission) {
                setUploadFile(null);
            }

            // Show success notification
            if (isMiddle || isPreparatory) {
                showNotification(
                    `${isTextSubmission ? 'Answers' : 'File'} submitted successfully! Please complete the self-assessment and peer feedback components.`,
                    "success"
                );
            } else {
                showNotification(
                    `${isTextSubmission ? 'Answers' : 'Assignment'} submitted successfully!`,
                    "success"
                );
            }

            // Refresh submission data to get the latest state including allow_resubmit flag
            await fetchSubmission(selectedAssignment.assignment_id);

        } catch (err) {
            console.error('Submission error:', err);
            showNotification(`Failed to submit ${isTextSubmission ? 'answers' : 'assignment'}: ${err.message}`, "error");
            throw err; // Re-throw to let the form handle loading state
        }
    };

    // Handler functions for self-assessment (foundational stage)
    const handleSelfAssessmentChange = (field, value) => {
        setSelfAssessmentForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNeededChange = (item) => {
        setSelfAssessmentForm(prev => {
            if (item === "none") {
                return {
                    ...prev,
                    needed: prev.needed.includes("none") ? [] : ["none"]
                };
            }

            let updatedNeeded = [...prev.needed];

            if (updatedNeeded.includes(item)) {
                updatedNeeded = updatedNeeded.filter(i => i !== item);
            } else {
                updatedNeeded.push(item);
                updatedNeeded = updatedNeeded.filter(i => i !== "none");
            }

            return {
                ...prev,
                needed: updatedNeeded
            };
        });
    };

    // Submit self-assessment
    const handleSelfAssessmentSubmit = async () => {
        try {
            setLoading(true);

            const formData = isMiddle ? middleSelfAssessmentForm : selfAssessmentForm;

            const response = await currentApiService.addSelfAssessment(
                selectedAssignment.assignment_id,
                formData
            );

            setSubmission(response);
            showNotification("Self-assessment submitted successfully!", "success");
        } catch (err) {
            showNotification(`Failed to submit self-assessment: ${err.message}`, "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handler functions for peer-assessment (foundational stage)
    const handlePeerAssessmentChange = (field, value) => {
        setPeerAssessmentForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFriendNeededChange = (item) => {
        setPeerAssessmentForm(prev => {
            if (item === "none") {
                return {
                    ...prev,
                    friend_needed: prev.friend_needed.includes("none") ? [] : ["none"]
                };
            }

            let updatedNeeded = [...prev.friend_needed];

            if (updatedNeeded.includes(item)) {
                updatedNeeded = updatedNeeded.filter(i => i !== item);
            } else {
                updatedNeeded.push(item);
                updatedNeeded = updatedNeeded.filter(i => i !== "none");
            }

            return {
                ...prev,
                friend_needed: updatedNeeded
            };
        });
    };

    // Handler functions for middle stage self-assessment
    const handleMiddleSelfAssessmentChange = (field, value) => {
        setMiddleSelfAssessmentForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handler functions for middle stage peer-assessment
    const handleMiddlePeerAssessmentChange = (field, value) => {
        setMiddlePeerAssessmentForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Submit peer assessment
    const handlePeerAssessmentSubmit = async (e) => {
        if (e) e.preventDefault();

        try {
            setLoading(true);

            const formData = isMiddle ? middlePeerAssessmentForm : peerAssessmentForm;

            const response = await currentApiService.addPeerAssessment(
                selectedAssignment.assignment_id,
                formData
            );

            setSubmission(response);
            showNotification("Peer assessment submitted successfully!", "success");
        } catch (err) {
            showNotification(`Failed to submit peer assessment: ${err.message}`, "error");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // Handle self-assessment submission complete (for preparatory/middle stage)
    const handleSelfAssessmentComplete = (updatedSubmission) => {
        setSubmission(updatedSubmission);
        showNotification("Self-assessment submitted successfully!", "success");
    };

    // Handle peer-assessment submission complete (for preparatory/middle stage)
    const handlePeerAssessmentComplete = (updatedSubmission) => {
        setSubmission(updatedSubmission);
        showNotification("Peer assessment submitted successfully!", "success");
    };

    if (!selectedAssignment) return null;

    return (
        <div style={{fontFamily: 'Poppins, sans-serif'}} className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={handleBack}
                        className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                    >
                        <ArrowLeft size={16} className="mr-2"/>
                        Back to Class
                    </button>
                    
                    <div className="bg-white rounded-2xl shadow-md border border-gray-200 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gray-50">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                    <Book className="w-5 h-5 text-white"/>
                                </div>
                                <h1 className="text-xl font-semibold text-gray-900 font-[poppins]">
                                    {selectedAssignment.title || "Assignment Details"}
                                </h1>
                            </div>
                        </div>
                        <div className="p-6">
                            <div className="flex flex-wrap items-center gap-4">
                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#D67C2C] bg-opacity-10 text-[#D67C2C] font-medium text-sm border border-[#D67C2C] border-opacity-20">
                                    {selectedAssignment.domain || "Assignment"} Domain
                                </span>
                                {selectedAssignment.due_date && (
                                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 font-medium text-sm border border-yellow-200">
                                        <Clock className="w-4 h-4 mr-2"/>
                                        Due: {formatDate(selectedAssignment.due_date)}
                                    </span>
                                )}
                                {selectedAssignment.created_at && (
                                    <span className="inline-flex items-center text-sm text-gray-600">
                                        <Clock className="w-4 h-4 mr-1 text-gray-400"/>
                                        Created: {formatDate(selectedAssignment.created_at)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Assignment Details Card */}
                <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
                    <div className="p-6 border-b border-gray-100 bg-gray-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                                <Book className="w-5 h-5 text-white"/>
                            </div>
                            <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Assignment Details</h2>
                        </div>
                    </div>

                    <div className="p-6">

                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D67C2C]"></div>
                            </div>
                        ) : (
                            <>
                                {/* Curricular Goals */}
                                {selectedAssignment.curricular_goals && selectedAssignment.curricular_goals.length > 0 && (
                                    <div className="mb-8">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <Target className="w-5 h-5 text-[#D67C2C] mr-2"/>
                                            Curricular Goals
                                        </h3>
                                        <div className="flex flex-wrap gap-3">
                                            {selectedAssignment.curricular_goals.map((goal, index) => (
                                                <span key={index}
                                                      className="inline-flex items-center gap-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg px-4 py-2 text-sm font-medium">
                                                    <Target size={14} className="text-orange-500"/>
                                                    {goal}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Competencies */}
                                {selectedAssignment.competencies && selectedAssignment.competencies.length > 0 && (
                                    <div className="mb-8">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <Award className="w-5 h-5 text-[#D67C2C] mr-2"/>
                                            Competencies
                                        </h3>
                                        <div className="flex flex-wrap gap-3">
                                            {selectedAssignment.competencies.map((competency, index) => (
                                                <span key={index}
                                                      className="bg-blue-50 text-blue-700 border border-blue-200 rounded-lg px-4 py-2 text-sm font-medium">
                                                    {competency}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Description */}
                                {selectedAssignment.description && (
                                    <div className="mb-8">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <CheckCircle className="w-5 h-5 text-[#D67C2C] mr-2"/>
                                            Description
                                        </h3>
                                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                                            <div className="text-gray-700 prose prose-sm max-w-none">
                                                <ReactMarkdown>{selectedAssignment.description}</ReactMarkdown>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Activity */}
                                {selectedAssignment.activity && (
                                    <div className="mb-8">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <Book className="w-5 h-5 text-[#D67C2C] mr-2"/>
                                            Activity
                                        </h3>
                                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                                            <div className="text-blue-800 prose prose-sm max-w-none">
                                                <ReactMarkdown>{selectedAssignment.activity}</ReactMarkdown>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Assessment Questions */}
                                {selectedAssignment.assessment_questions && selectedAssignment.assessment_questions.length > 0 && (
                                    <div className="mb-8">
                                        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                            <Award className="w-5 h-5 text-[#D67C2C] mr-2"/>
                                            Assessment Questions
                                        </h3>
                                        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                                            <ol className="text-green-800 space-y-3 list-decimal list-inside">
                                                {selectedAssignment.assessment_questions.map((question, index) => (
                                                    <li key={index} className="font-medium">{question}</li>
                                                ))}
                                            </ol>
                                        </div>
                                    </div>
                                )}

                                {/* Stage Indicators */}
                                <div className="flex gap-3">
                                    {isPreparatory && (
                                        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
                                            Preparatory Stage
                                        </span>
                                    )}
                                    {isMiddle && (
                                        <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-teal-100 text-teal-800 border border-teal-200">
                                            Middle Stage
                                        </span>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Assessment Forms Section */}
                {!loading && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* Submission Form */}
                        <div className="lg:col-span-2">
                            <SubmissionForm
                                assignment={selectedAssignment}
                                submission={submission}
                                uploadFile={uploadFile}
                                handleFileChange={handleFileChange}
                                handleClearUploadFile={handleClearUploadFile}
                                handleSubmit={handleSubmit}
                                isPreparatory={isPreparatory}
                                isMiddle={isMiddle}
                            />
                        </div>

                        {/* Feedback Section */}
                        <div className="space-y-6">
                            <TeacherFeedback submission={submission} selectedClass={selectedClass}/>
                            <SubmissionStatus
                                submission={submission}
                                isPreparatory={isPreparatory}
                                isMiddle={isMiddle}
                            />
                        </div>
                    </div>
                )}

                {/* Assessment Wrapper */}
                {!loading && (
                    <div className="mt-8">
                        <AssessmentWrapper
                            selectedClass={selectedClass}
                            assignmentId={selectedAssignment.assignment_id}
                            submission={submission}
                            isMiddle={isMiddle}
                            selfAssessmentForm={selfAssessmentForm}
                            handleSelfAssessmentChange={handleSelfAssessmentChange}
                            handleNeededChange={handleNeededChange}
                            handleSelfAssessmentSubmit={handleSelfAssessmentSubmit}
                            peerAssessmentForm={peerAssessmentForm}
                            handlePeerAssessmentChange={handlePeerAssessmentChange}
                            handleFriendNeededChange={handleFriendNeededChange}
                            handlePeerAssessmentSubmit={handlePeerAssessmentSubmit}
                            middleSelfAssessmentForm={middleSelfAssessmentForm}
                            handleMiddleSelfAssessmentChange={handleMiddleSelfAssessmentChange}
                            middlePeerAssessmentForm={middlePeerAssessmentForm}
                            handleMiddlePeerAssessmentChange={handleMiddlePeerAssessmentChange}
                            onSelfAssessmentComplete={handleSelfAssessmentComplete}
                            onPeerAssessmentComplete={handlePeerAssessmentComplete}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssignmentDetailsView;